import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getRoleDashboardPath } from '../../utils/roleDashboard';
import {
  Keyboard, CheckCircle2, Undo2, Globe, Building2,
  ShieldCheck, FileSearch, Loader2,
} from 'lucide-react';
import FinalReportReviewModal from './FinalReportReviewModal';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

export const TYPING_SOURCE_META = {
  title_search_internal: { label: 'Search', icon: FileSearch, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  title_search_external: { label: 'Search', icon: FileSearch, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  qc_approved: { label: 'QC Approved', icon: ShieldCheck, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};

export const TYPING_STATUS_META = {
  pending: { label: 'Pending Typing', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  returned: { label: 'Returned', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  returned_to_qc: { label: 'Returned to QC', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const fmtDT = (iso) => (iso ? formatDateTimeLocal(iso) : null);

const InfoItem = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <span className="text-gray-500 uppercase font-semibold block" style={{ fontSize: '9px', letterSpacing: '0.06em' }}>{label}</span>
    <span className="text-gray-900 font-medium" style={{ fontSize: '11.5px' }}>{value || '—'}</span>
  </div>
);

/**
 * Typing block for the shared Order Summary modal.
 * Read-only typing context + state-driven typing actions that call the
 * existing Phase 2 typing endpoints (claim/start/complete/return/unassign).
 */
export const TypingOrderSection = ({ orderData, currentUser, onUpdate, onClose, showActions = true, bottomBar = false }) => {
  const [order, setOrder] = useState(orderData);
  const [busy, setBusy] = useState(null);
  const [showReturn, setShowReturn] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  // TYPING-RETURN-TO-QC-001 — where this return will go (QC approver vs Search Processor)
  const [returnPreview, setReturnPreview] = useState(null);
  useEffect(() => {
    if (!showReturn || !orderData?.id) { setReturnPreview(null); return; }
    axios.get(`${API}/api/orders/${orderData.id}/typing/return-preview`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setReturnPreview(r.data)).catch(() => setReturnPreview({ route: 'unknown' }));
  }, [showReturn, orderData?.id]);
  const returnHint = !returnPreview ? 'Checking where this file will be routed…'
    : returnPreview.route === 'qc'
      ? (returnPreview.target_user_id
        ? `This file goes back to QC Specialist ${returnPreview.target_user_name} (who approved it) for re-review and leaves the Typing queue. The Search Processor is not involved unless QC returns it.`
        : `This file goes back to the unassigned QC queue for re-review (original QC Specialist ${returnPreview.approver_name || ''} unavailable: ${returnPreview.fallback_reason || 'not eligible'}). Team Leaders are notified.`)
      : 'The file goes back to the Search Processor (pending correction) and leaves the Typing queue.';
  const [reviewPrep, setReviewPrep] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { setOrder(orderData); }, [orderData]);

  const role = currentUser?.role;
  const isAdmin = role === 'admin';
  const isTypist = role === 'typing_processor';
  const canAct = (isAdmin || isTypist) && showActions;
  const mine = order?.typing_assigned_to === currentUser?.id;
  // TYPING-COMPLETE-FIX-001 — a completed typing stage must never look
  // claimable again, even from a stale parent snapshot.
  const typingDone = order?.typing_status === 'completed';
  const inTyping = order?.status === 'typing' && !typingDone;

  if (!order?.typing_entered_at && order?.status !== 'typing') return null;

  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  // TYPING-COMPLETE-FIX-001 — refetch the persisted order and push it UP to
  // the parent (App.js merges partial patches; bare onUpdate() carried nothing,
  // so parent re-renders reverted this card to the page-load snapshot).
  const refreshFromServer = async () => {
    try {
      const resp = await axios.get(`${API}/api/orders/${order.id}/details`, authHeader);
      if (resp?.data?.order) {
        setOrder(prev => ({ ...prev, ...resp.data.order }));
        if (onUpdate) onUpdate(resp.data.order);
        return resp.data.order;
      }
    } catch { /* parent queue refresh still handles list views */ }
    if (onUpdate) onUpdate();
    return null;
  };

  // After completion/return on the full order page (no modal to close),
  // send the typist back to the Typing Dashboard to take the next file.
  const exitAfterFinalAction = () => {
    if (onClose) onClose();
    else if (isTypist) navigate(getRoleDashboardPath(currentUser), { replace: true });
  };

  // TYPING-REVIEW-001 — "Complete Typing" only opens the Final Report Review;
  // the actual completion happens from the modal's "Submit & Complete".
  const openFinalReview = async () => {
    setBusy('complete');
    try {
      const resp = await axios.post(`${API}/api/orders/${order.id}/typing/prepare-completion`, {}, authHeader);
      setReviewPrep(resp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the final report review');
    } finally {
      setBusy(null);
    }
  };

  const act = async (action, body = {}) => {
    setBusy(action);
    try {
      const resp = await axios.post(`${API}/api/orders/${order.id}/typing/${action}`, body, authHeader);
      if (action === 'complete') {
        const d = resp?.data?.delivery;
        if (d?.mode === 'internal_email' && d?.recipient) {
          toast.success(`Typing completed — '${d.document}' is being delivered to ${d.recipient}`);
        } else if (d?.mode === 'internal_email') {
          toast.success('Typing completed — no delivery email configured; delivery is pending manual dispatch');
        } else {
          toast.success('Typing completed — External Portal order (delivery via client portal)');
        }
      }
      else if (action === 'assign-to-me') toast.success('File assigned to you');
      else if (action === 'start') toast.success('Typing started');
      else if (action === 'return') {
        const rt = resp?.data;
        toast.success(rt?.routed_to_stage === 'qc'
          ? (rt.routed_to ? `File returned to QC Specialist ${rt.routed_to_name} for re-review` : 'File returned to the unassigned QC queue for re-review')
          : 'File returned to the Search Processor for correction');
      }
      else toast.success('Done');
      setShowReturn(false); setReturnReason('');
      await refreshFromServer();
      if (action === 'complete' || action === 'return') {
        exitAfterFinalAction();
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Typing action failed');
    } finally {
      setBusy(null);
    }
  };

  const src = TYPING_SOURCE_META[order.typing_source] || { label: order.typing_source || '—', icon: FileSearch, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  const st = TYPING_STATUS_META[order.typing_status] || TYPING_STATUS_META.pending;
  const SrcIcon = src.icon;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200" data-testid="typing-order-section">
      <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2.5 flex-wrap">
          <span className="h-6 w-6 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Keyboard className="h-3.5 w-3.5 text-white" />
          </span>
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">Typing</span>
          <span className={`text-[10.5px] font-semibold border rounded-full px-2 py-0.5 ${st.cls}`} data-testid="typing-section-status">{st.label}</span>
          <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold border rounded-full px-2 py-0.5 ${src.cls}`} data-testid="typing-section-source">
            <SrcIcon className="h-3 w-3" /> {src.label}
          </span>
          {order.typing_is_external === true && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-purple-700"><Globe className="h-3 w-3" /> External</span>
          )}
          {order.typing_is_external === false && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-600"><Building2 className="h-3 w-3" /> Internal</span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-1">
          <InfoItem label="Entered Typing" value={fmtDT(order.typing_entered_at)} testid="typing-section-entered" />
          <InfoItem label="Assigned Typist" value={order.typing_assigned_to_name || 'Unassigned'} testid="typing-section-typist" />
          <InfoItem label="Typing Started" value={(order.typing_assigned_to || order.typing_status !== 'pending') ? fmtDT(order.typing_started_at) : null} testid="typing-section-started" />
          {order.typing_completed_at && (
            <InfoItem label="Typing Completed" value={`${order.typing_completed_by_name ? order.typing_completed_by_name + ' · ' : ''}${fmtDT(order.typing_completed_at)}`} testid="typing-section-completed" />
          )}
          {order.search_work_completed_at && (
            <InfoItem label="Search Completed" value={`${order.search_work_completed_by_name ? order.search_work_completed_by_name + ' · ' : ''}${fmtDT(order.search_work_completed_at)}`} testid="typing-section-search-completed" />
          )}
          {order.qc_approved_at && (
            <InfoItem label="QC Approved" value={`${order.qc_approved_by_name ? order.qc_approved_by_name + ' · ' : ''}${fmtDT(order.qc_approved_at)}`} testid="typing-section-qc-approved" />
          )}
        </div>

        {canAct && inTyping && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2.5 pt-2.5 border-t border-blue-100">
            {/* TYPING-CLAIM-QUEUE-001: claiming happens on the Typing Dashboard
                queue only — no Claim action inside the order. */}
            {!order.typing_assigned_to && isTypist && (
              <span className="text-[11.5px] text-slate-500 italic" data-testid="typing-section-claim-hint">
                Unassigned — claim this file from the Typing Dashboard queue.
              </span>
            )}
            {/* TYPING-ENTRY-001 (Sep 2026): "Start Typing" removed from Order
                Details for every role — Typing is started and worked only from
                the dedicated Typing Dashboard. Status card stays read-only here. */}
            {mine && order.typing_status === 'pending' && (
              <span className="text-[11.5px] text-slate-500 italic" data-testid="typing-section-start-hint">
                Pending —{' '}
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/typing')}
                  className="not-italic font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2"
                  data-testid="typing-section-dashboard-link"
                >
                  start this file from the Typing Dashboard
                </button>.
              </span>
            )}
            {/* State matrix: In Progress → Return + Complete.
                When bottomBar is active these move to the Search-style fixed
                action bar below; otherwise they render inline here. */}
            {!bottomBar && order.typing_assigned_to && (mine || isAdmin) && order.typing_status === 'in_progress' && (
              <>
                <button onClick={() => { setShowReturn(v => !v); setReturnReason(''); }}
                  className="px-3 py-1.5 text-[11.5px] font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-md transition-colors inline-flex items-center gap-1"
                  data-testid="typing-section-return-btn"><Undo2 className="h-3 w-3" /> Return</button>
                <button
                  onClick={openFinalReview}
                  disabled={busy === 'complete'}
                  className="px-3 py-1.5 text-[11.5px] font-semibold text-white rounded-md transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                  style={{ background: '#004aad' }}
                  data-testid="typing-section-complete-btn"><CheckCircle2 className="h-3 w-3" /> Complete</button>
              </>
            )}
            {order.typing_assigned_to && (mine || isAdmin) && (
              <button onClick={() => act('unassign')} disabled={busy === 'unassign'}
                className="px-3 py-1.5 text-[11.5px] font-semibold text-slate-500 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
                data-testid="typing-section-unassign-btn">Unassign</button>
            )}
          </div>
        )}

        {!bottomBar && showReturn && (
          <div className="mt-2.5 bg-white border border-rose-200 rounded-lg p-3 space-y-2" data-testid="typing-section-return-panel">
            <p className="text-[11px] text-slate-500" data-testid="typing-return-route-hint" data-route={returnPreview?.route || 'loading'}>{returnHint}</p>
            <textarea rows={2} value={returnReason} onChange={e => setReturnReason(e.target.value)}
              placeholder="Describe the data problem found while typing (min 10 characters)…"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-rose-400"
              data-testid="typing-section-return-reason" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReturn(false)} className="px-3 py-1.5 text-[11.5px] font-semibold border border-slate-200 text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
              <button disabled={returnReason.trim().length < 10 || busy === 'return'}
                onClick={() => act('return', { reason: returnReason.trim() })}
                className="px-3 py-1.5 text-[11.5px] font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-md disabled:opacity-50"
                data-testid="typing-section-return-submit">Return File</button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════ Search-style fixed bottom action bar (Fulfillment) ═══════════
          TYPING-ACTIONBAR-001 — mirrors the Search Processor sticky action bar.
          Return + Complete only; state-driven (In Progress). Complete opens the
          Final Report Review (no immediate completion). Unassign stays inline
          above. Backend workflow/endpoints unchanged. */}
      {bottomBar && canAct && inTyping && order.typing_assigned_to && (mine || isAdmin) && order.typing_status === 'in_progress' && (
        <>
          {showReturn && (
            <div className="fixed bottom-[60px] left-0 right-0 z-40 px-6" data-testid="typing-section-return-panel">
              <div className="max-w-[1440px] mx-auto bg-white border border-rose-200 rounded-lg p-3 space-y-2 shadow-lg">
                <p className="text-[11px] text-slate-500" data-testid="typing-return-route-hint" data-route={returnPreview?.route || 'loading'}>{returnHint}</p>
                <textarea rows={2} value={returnReason} onChange={e => setReturnReason(e.target.value)}
                  placeholder="Describe the data problem found while typing (min 10 characters)…"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-rose-400"
                  data-testid="typing-section-return-reason" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowReturn(false)} className="px-3 py-1.5 text-[11.5px] font-semibold border border-slate-200 text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
                  <button disabled={returnReason.trim().length < 10 || busy === 'return'}
                    onClick={() => act('return', { reason: returnReason.trim() })}
                    className="px-3 py-1.5 text-[11.5px] font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-md disabled:opacity-50"
                    data-testid="typing-section-return-submit">Return File</button>
                </div>
              </div>
            </div>
          )}
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]" data-testid="typing-action-bar">
            <div className="max-w-[1440px] mx-auto px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                  <Keyboard className="h-3.5 w-3.5 text-blue-600" /> Typing
                </span>
                <span className={`text-[10.5px] font-semibold border rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                {order.typing_is_external === true && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700"><Globe className="h-3 w-3" /> External</span>
                )}
                {order.typing_is_external === false && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600"><Building2 className="h-3 w-3" /> Internal</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowReturn(v => !v); setReturnReason(''); }}
                  className="text-[12px] h-8 px-3 rounded-md font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
                  data-testid="typing-section-return-btn"><Undo2 className="h-3.5 w-3.5" /> Return</button>
                <button
                  onClick={openFinalReview}
                  disabled={busy === 'complete'}
                  className="text-[12px] h-8 px-3 rounded-md font-semibold text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="typing-section-complete-btn">
                  {busy === 'complete' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Complete</button>
              </div>
            </div>
          </div>
        </>
      )}

      {reviewPrep && (
        <FinalReportReviewModal
          order={order}
          prep={reviewPrep}
          onClose={() => setReviewPrep(null)}
          onDeliveryFailed={async () => { setReviewPrep(null); await refreshFromServer(); }}
          onCompleted={async () => {
            setReviewPrep(null);
            await refreshFromServer();
            exitAfterFinalAction();
          }}
        />
      )}
    </div>
  );
};

export default TypingOrderSection;
